import React, { useState, useCallback, useEffect } from 'react';
import './App.css';
import axios from 'axios';

// Context
import { ThemeProvider, useTheme } from './context/ThemeContext';

// Components
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import DashboardView from './components/dashboard/DashboardView';
import DataTable from './components/analysis/DataTable';
import ManagerAnalysis from './components/analysis/ManagerAnalysis';
import DataUploadView from './components/upload/DataUploadView';
import FloatingAIButton from './components/ui/FloatingAIButton';
import DataQualityReport from './components/analysis/DataQualityReport';
import IdealClientProfile from './components/analysis/IdealClientProfile';
import WhatIfAnalyzer from './components/analysis/WhatIfAnalyzer';
import PlanGroups from './components/analysis/PlanGroups';
import WorkloadDecisionAgent from './components/analysis/WorkloadDecisionAgent';
import SettingsPanel from './components/settings/SettingsPanel';
import PlanConfiguration from './components/settings/PlanConfiguration';
import PlanTimeline from './components/analysis/PlanTimeline';
import RevenueSecurity from './components/analysis/RevenueSecurity';
import RevenueTimeline from './components/analysis/RevenueTimeline';
import RevenueTracker from './components/analysis/RevenueTracker';
import ClientLadder from './components/analysis/ClientLadder';
import RevenuePerLot from './components/analysis/RevenuePerLot';
import TKAnalyst from './components/analysis/TKAnalyst';
import RiskRegister from './components/analysis/RiskRegister';
import NPSAnalysis from './components/analysis/NPSAnalysis';
import PlanGroupDeepDive from './components/analysis/PlanGroupDeepDive';
import TKValueProposition from './components/marketing/TKValueProposition';
import ProposalPage from './components/marketing/ProposalPage';
import ManagerDeepDive from './components/analysis/ManagerDeepDive';
import TeamLeaderDeepDive from './components/analysis/TeamLeaderDeepDive';
import PerformanceLadder from './components/analysis/PerformanceLadder';
import WorkloadAnalysis from './components/analysis/WorkloadAnalysis';
import TeamStructureConfig from './components/config/TeamStructureConfig';
import LoginScreen, { isAuthenticated, logout } from './components/auth/LoginScreen';

// Fallback mock data
import { REVENUE_DATA, COMPLIANCE_DATA, WORKLOAD_DATA, NPS_DATA } from './data/mockData';
import { processClientData, calculateMetrics } from './utils/dataProcessing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AppContent() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Authentication State
  const [authenticated, setAuthenticated] = useState(isAuthenticated());
  
  // Config State (expanded for meeting costs and annualization)
  const [config, setConfig] = useState({
    costPerHour: 160,
    comMeetingCost: 1000,
    agmMeetingCost: 2500,
    specialMeetingCost: 1000,
    annualizationMultiplier: 3
  });
  
  // Data State
  const [clients, setClients] = useState([]);
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    totalMessageCost: 0,
    totalHandleTimeCost: 0,
    totalWorkloadCost: 0,
    avgNPS: 0,
    avgCompliance: 0,
    totalPlans: 0
  });
  const [loading, setLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false);
  
  // Navigation State
  const [currentView, setCurrentView] = useState('tk-analyst');
  const [analysisCategory, setAnalysisCategory] = useState('manager');
  const [showConfig, setShowConfig] = useState(false);
  const [deepDiveGroup, setDeepDiveGroup] = useState(null); // pre-selected group from global search
  const [deepDiveSubject, setDeepDiveSubject] = useState(null); // pre-selected manager/TL from Performance Ladder

  const handleGlobalSearchNavigate = useCallback((basePlan) => {
    setDeepDiveGroup(basePlan);
    setCurrentView('deep-dive');
  }, []);

  // Used by the Performance Ladder to deep-link directly into the matching
  // Manager / Team-Leader Deep Dive with the row's subject pre-selected.
  const openSubjectDeepDive = useCallback((subjectType, name) => {
    setDeepDiveSubject({ subjectType, name });
    setCurrentView(subjectType === 'Team Leader' ? 'team-leader-deep-dive' : 'manager-deep-dive');
  }, []);

  // Fetch data from API
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/data`);
      const data = response.data;
      
      setClients(data.clients);
      setMetrics(data.metrics);
      setConfig(data.config);
      setUseMockData(false);
    } catch (error) {
      console.error('Failed to fetch data from API, using mock data:', error);
      // Fallback to mock data
      const mockMerged = processClientData(REVENUE_DATA, COMPLIANCE_DATA, WORKLOAD_DATA, NPS_DATA, config.costPerHour);
      const mockMetrics = calculateMetrics(mockMerged, NPS_DATA);
      setClients(mockMerged);
      setMetrics(mockMetrics);
      setUseMockData(true);
    } finally {
      setLoading(false);
    }
  }, [config.costPerHour]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update config settings
  const handleConfigChange = async (newConfig) => {
    setConfig(prev => ({ ...prev, ...newConfig }));
    
    try {
      await axios.put(`${API}/config`, newConfig);
      // Refetch data with new config
      fetchData();
    } catch (error) {
      console.error('Failed to update config:', error);
    }
  };

  const handleAnalysisTab = (cat) => {
    setCurrentView('analysis');
    setAnalysisCategory(cat);
  };

  const handleCustomData = async (files) => {
    try {
      // Helper to append file to FormData
      const appendFile = (formData, file, name) => {
        if (file instanceof File) {
          formData.append('file', file, file.name);
        } else if (file) {
          // Fallback for text content (legacy)
          formData.append('file', new Blob([file], { type: 'text/csv' }), `${name}.csv`);
        }
      };

      // Upload masterlist first (Excel file) - highest priority for manager assignments
      if (files.masterlist) {
        const mlData = files.masterlist;
        const mlFile = mlData.file || mlData;
        const merge = mlData.merge !== undefined ? mlData.merge : false;
        const formData = new FormData();
        appendFile(formData, mlFile, 'masterlist');
        const mergeParam = merge ? '?merge=true' : '';
        await axios.post(`${API}/upload/masterlist${mergeParam}`, formData);
      }
      if (files.revenue) {
        const formData = new FormData();
        appendFile(formData, files.revenue, 'revenue');
        await axios.post(`${API}/upload/revenue`, formData);
      }
      if (files.compliance) {
        const formData = new FormData();
        appendFile(formData, files.compliance, 'compliance');
        await axios.post(`${API}/upload/compliance`, formData);
      }
      if (files.workload) {
        const formData = new FormData();
        appendFile(formData, files.workload, 'workload');
        await axios.post(`${API}/upload/workload`, formData);
      }
      if (files.nps) {
        const formData = new FormData();
        appendFile(formData, files.nps, 'nps');
        await axios.post(`${API}/upload/nps`, formData);
      }
      if (files.meetings) {
        const formData = new FormData();
        appendFile(formData, files.meetings, 'meetings');
        await axios.post(`${API}/upload/meetings`, formData);
      }
      if (files.calls) {
        const formData = new FormData();
        appendFile(formData, files.calls, 'calls');
        await axios.post(`${API}/upload/calls`, formData);
      }
      if (files.everperform) {
        const formData = new FormData();
        appendFile(formData, files.everperform, 'everperform');
        await axios.post(`${API}/upload/everperform`, formData);
      }
      if (files.revenueTracker) {
        const rt = files.revenueTracker;
        if (rt.fy26) {
          const formData = new FormData();
          appendFile(formData, rt.fy26, 'revenue-tracker-fy26');
          await axios.post(`${API}/upload/revenue-tracker?fyOverride=FY26`, formData);
        }
        if (rt.fy27) {
          const formData = new FormData();
          appendFile(formData, rt.fy27, 'revenue-tracker-fy27');
          await axios.post(`${API}/upload/revenue-tracker?fyOverride=FY27`, formData);
        }
      }
      
      await fetchData();
    } catch (error) {
      console.error('Failed to upload files:', error);
      throw error;
    }
  };

  const getCategoryLabel = () => {
    if (analysisCategory === 'tl') return 'Team Leader';
    return analysisCategory.charAt(0).toUpperCase() + analysisCategory.slice(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin mb-4" />
          <p className="text-sm text-[#737373]">Loading TK Analytics...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!authenticated) {
    return <LoginScreen onLogin={() => setAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen flex font-sans antialiased bg-[#F8F9FA] text-[#171717]">
      {/* Sidebar */}
      <Sidebar 
        currentView={currentView}
        setCurrentView={(view) => {
          if (view === 'settings') {
            setShowConfig(true);
          } else {
            setCurrentView(view);
          }
        }}
        onLogout={() => {
          localStorage.removeItem('tk_authenticated');
          setAuthenticated(false);
        }}
      />

      {/* Main Content */}
      <main className="flex-1 overflow-auto ml-60 bg-[#F8F9FA]">
        {/* Header */}
        <Header 
          currentView={currentView}
          analysisCategory={analysisCategory}
          config={config}
          useMockData={useMockData}
          onGlobalSearchNavigate={handleGlobalSearchNavigate}
        />

        <div className="p-6 lg:p-8 max-w-[1600px] mx-auto space-y-6">
          {currentView === 'tk-analyst' ? (
            <TKAnalyst />
          ) : currentView === 'dashboard' ? (
            <DashboardView data={clients} metrics={metrics} />
          ) : currentView === 'upload' ? (
            <DataUploadView onProcess={handleCustomData} />
          ) : currentView === 'data-quality' ? (
            <DataQualityReport />
          ) : currentView === 'ideal-client' ? (
            <IdealClientProfile />
          ) : currentView === 'what-if' ? (
            <WhatIfAnalyzer />
          ) : currentView === 'plan-groups' ? (
            <PlanGroups />
          ) : currentView === 'workload-agent' ? (
            <WorkloadDecisionAgent />
          ) : currentView === 'plan-config' ? (
            <PlanConfiguration />
          ) : currentView === 'plan-timeline' ? (
            <PlanTimeline />
          ) : currentView === 'revenue-timeline' ? (
            <RevenueTimeline />
          ) : currentView === 'revenue-tracker' ? (
            <RevenueTracker />
          ) : currentView === 'client-ladder' ? (
            <ClientLadder />
          ) : currentView === 'risk-register' ? (
            <RiskRegister />
          ) : currentView === 'revenue-per-lot' ? (
            <RevenuePerLot />
          ) : currentView === 'nps-analysis' ? (
            <NPSAnalysis />
          ) : currentView === 'deep-dive' ? (
            <PlanGroupDeepDive initialGroup={deepDiveGroup} onGroupChange={setDeepDiveGroup} />
          ) : currentView === 'value-proposition' ? (
            <TKValueProposition />
          ) : currentView === 'proposal' ? (
            <ProposalPage />
          ) : currentView === 'managers' ? (
            <ManagerAnalysis data={clients} />
          ) : currentView === 'performance-ladder' ? (
            <PerformanceLadder onOpenSubject={openSubjectDeepDive} />
          ) : currentView === 'workload-analysis' ? (
            <WorkloadAnalysis />
          ) : currentView === 'manager-deep-dive' ? (
            <ManagerDeepDive
              initialManager={deepDiveSubject?.subjectType === 'Manager' ? deepDiveSubject.name : null}
              onConsumed={() => setDeepDiveSubject(null)}
            />
          ) : currentView === 'team-leader-deep-dive' ? (
            <TeamLeaderDeepDive
              initialLeader={deepDiveSubject?.subjectType === 'Team Leader' ? deepDiveSubject.name : null}
              onConsumed={() => setDeepDiveSubject(null)}
            />
          ) : currentView === 'team-structure' ? (
            <TeamStructureConfig />
          ) : currentView === 'analysis' ? (
            <DataTable 
              data={clients} 
              category={getCategoryLabel()}
              onDeletePlans={fetchData}
            />
          ) : (
            <DashboardView data={clients} metrics={metrics} />
          )}
        </div>
        
        {/* Settings Panel */}
        <SettingsPanel
          isOpen={showConfig}
          onClose={() => setShowConfig(false)}
          config={config}
          onConfigChange={handleConfigChange}
        />
      </main>

      {/* Floating AI action — sister-platform signature, hides on TK Analyst itself */}
      <FloatingAIButton
        hidden={currentView === 'tk-analyst'}
        onClick={() => setCurrentView('tk-analyst')}
      />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
